import { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import "../../../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";
import "../../../UserDashboard/components/StatementTable/StatementTable.css";
import localeText from "../../../../locale";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { formatPrice } from "../../../../utilities/formatPrice";
import ExtraPayService from "../../../../service/ExtraPayService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";

const SUCCESS_CODE = 200001;

const getWithdrawId = (row) => row?.id ?? row?.idwalletTokensWithdrawRequest;

const redirectToLogin = () => {
  localStorage.removeItem("__extraconomy_token");
  localStorage.removeItem("__extraconomy_username");
  window.location.replace("/login");
};

function formatDate(date) {
  date = new Date(date);
  if (Number.isNaN(date.getTime())) return "";

  const dd = date.getUTCDate();
  const mm = date.getUTCMonth() + 1;
  const yy = date.getUTCFullYear();

  return dd + "/" + mm + "/" + yy;
}

const ListExcWithdrawRequests = () => {
  const [loading, setLoading] = useState(false);
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [filteredWithdrawRequests, setFilteredWithdrawRequests] = useState([]);
  const [selectedWithdrawRequest, setSelectedWithdrawRequest] = useState("");
  const [processVisible, setProcessVisible] = useState(false);
  const [currentWithdrawRequest, setCurrentWithdrawRequest] = useState(null);
  const { showErrorToast, showSuccessToast } = useToastContext();

  useEffect(() => {
    let cancel = false;

    fetchWithdrawRequests(cancel);

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchWithdrawRequests(cancel) {
    try {
      setLoading(true);
      const extraPayService = new ExtraPayService(localStorage.getItem("__extraconomy_token"));
      const res = await extraPayService.listAllWithdrawals(
        localStorage.getItem("__extraconomy_username"),
      );
      if (cancel) return;

      if (res.messageCode !== SUCCESS_CODE) {
        showErrorToast({ code: res.messageCode });
        setLoading(false);
        return;
      }

      const rows = (Array.isArray(res.result) ? res.result : []).map((row) => ({
        ...row,
        id: getWithdrawId(row),
      }));
      setWithdrawRequests(rows);
      setFilteredWithdrawRequests(rows);
      setLoading(false);
    } catch (e) {
      if (
        e.status === 401 ||
        e.status === 403 ||
        e.response?.status === 401 ||
        e.response?.status === 403
      ) {
        redirectToLogin();
        return;
      }
      showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
      setLoading(false);
      console.log(e);
    }
  }

  async function processRequest(id) {
    try {
      setLoading(true);
      const extraPayService = new ExtraPayService(localStorage.getItem("__extraconomy_token"));
      const res = await extraPayService.processWithdrawal({
        idwalletTokensWithdrawRequest: id,
        useradmin: { username: localStorage.getItem("__extraconomy_username") },
      });

      if (res.messageCode !== SUCCESS_CODE) {
        showErrorToast({ code: res.messageCode });
        setLoading(false);
        return;
      }

      showSuccessToast({ detail: "excWithdrawProcessedSuccessfully" });
      fetchWithdrawRequests(false);
    } catch (e) {
      if (
        e.status === 401 ||
        e.status === 403 ||
        e.response?.status === 401 ||
        e.response?.status === 403
      ) {
        redirectToLogin();
        return;
      }
      showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
      setLoading(false);
      console.log(e);
    }
  }

  const filterByWithdrawRequests = (withdraw) => {
    if (withdraw) {
      const query = withdraw.toLowerCase();
      const filteredData = withdrawRequests.filter((obj) => {
        return (
          obj?.username?.toLowerCase().includes(query) ||
          obj?.email?.toLowerCase().includes(query) ||
          obj?.addressEXC?.toLowerCase().includes(query)
        );
      });
      setFilteredWithdrawRequests([...filteredData]);
    } else {
      setFilteredWithdrawRequests(withdrawRequests);
    }
  };

  return (
    <div className="card border-1 border-300">
      <ConfirmDialog
        visible={processVisible}
        onHide={() => {
          setCurrentWithdrawRequest(null);
          setProcessVisible(false);
        }}
        message={localeText("processThisExcWithdrawRequest")}
        header={localeText("confirmYourAction")}
        icon="pi pi-exclamation-triangle"
        accept={() => processRequest(getWithdrawId(currentWithdrawRequest))}
        reject={() => {
          setCurrentWithdrawRequest(null);
          setProcessVisible(false);
        }}
        acceptLabel={localeText("confirm")}
        rejectLabel={localeText("cancel")}
      />

      <h3 className="my-4">{localeText("excWithdrawRequests")}</h3>

      <p>{localeText("searchByUsernameEmailAddress")}:</p>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "justify-content",
          alignItems: "center",
        }}
      >
        <InputText
          className="w-100"
          name="tracking"
          value={selectedWithdrawRequest}
          onChange={(e) => {
            setSelectedWithdrawRequest(e.target.value);
            filterByWithdrawRequests(e.target.value);
          }}
          placeholder={localeText("search")}
        />
      </div>
      <hr />

      <DataTable
        className="mt-4 mb-6 p-datatable-customers"
        value={filteredWithdrawRequests}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        dataKey="id"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column
          header={`${localeText("username")} / ${localeText("email")}`}
          body={(withdrawRequest) => {
            return (
              <>
                <p className="mb-1 mt-0">{withdrawRequest.username || "—"}</p>
                <p className="mb-0 mt-0">{withdrawRequest.email || "—"}</p>
              </>
            );
          }}
        />
        <Column
          header={localeText("amountRequested")}
          body={(withdrawRequest) => <p>EXC {formatPrice(Number(withdrawRequest.amount) || 0)}</p>}
        />
        <Column
          header={localeText("extrapayFlowWalletAddress")}
          body={(withdrawRequest) => <p className="mb-0">{withdrawRequest.addressEXC || "—"}</p>}
        />
        <Column
          header={localeText("requestDate")}
          body={(withdrawRequest) => (
            <>
              {withdrawRequest.dateRequest && <div>{formatDate(withdrawRequest.dateRequest)}</div>}
            </>
          )}
        />
        <Column
          header={localeText("status")}
          body={(withdrawRequest) => {
            if (withdrawRequest.status === "COMPLETED") {
              return (
                <span
                  className="team-member-role text-0 p-1 border-round"
                  style={{ backgroundColor: "#46a748" }}
                >
                  {localeText("completed").toUpperCase()}
                </span>
              );
            }
            if (withdrawRequest.status === "REQUESTED") {
              return (
                <span
                  className="team-member-role text-0 p-1 border-round"
                  style={{ backgroundColor: "#F7B005" }}
                >
                  {localeText("extrapayFlowStatusRequested").toUpperCase()}
                </span>
              );
            }
            return (
              <span
                className="team-member-role text-0 p-1 border-round"
                style={{ backgroundColor: "#F7B005" }}
              >
                {(withdrawRequest.status || "").toUpperCase()}
              </span>
            );
          }}
        />
        <Column
          header={localeText("processRequest")}
          body={(withdrawRequest) => {
            if (withdrawRequest.status !== "REQUESTED") return null;
            return (
              <Button
                label={localeText("markExcWithdrawCompleted")}
                icon="pi pi-check"
                className="p-button-success p-button-rounded"
                onClick={() => {
                  setProcessVisible(true);
                  setCurrentWithdrawRequest(withdrawRequest);
                }}
              />
            );
          }}
        />
      </DataTable>
    </div>
  );
};

export default ListExcWithdrawRequests;
